/**
 * Entity Registry System
 * 
 * Provides a centralized registry for entity definitions used in the template editor.
 * Entities represent data sources that can be bound to template elements.
 */

import React from "react";
import { MediaType } from "@/page-components/post-generator/core/template.schemas";
import { OctagonAlert } from 'lucide-react';
import { ApiRequestOptions } from "@/lib/api/client";

/**
 * Defines an attribute of an entity type
 */
export type AttributeDef = {
  /** Unique key for the attribute (e.g., 'name', 'logoPath', 'kickoff') */
  attributeKey: string;
  /** Display label for UI (e.g., 'Teamname', 'Logo') */
  attributeLabel: string;
  /** Data type of the attribute */
  mediaType: MediaType;
  /** Default value for the attribute */
  defaultValue?: any;
  /** Whether this attribute is required */
  required?: boolean;
  /** Additional validation rules */
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
};

/**
 * Standardized search result structure for entity searches
 * Contains minimal information needed for selection UI
 */
export type SearchResult = {
  /** Unique identifier of the entity */
  id: string;
  /** Display label for the entity */
  label: string;
  /** Optional subtitle for additional context */
  subTitle?: string;
  /** Optional thumbnail URL for selection previews */
  thumbnailUrl?: string;
};

/**
 * Defines a relationship between entity types
 */
export type RelationDef = {
  /** Relationship key (e.g., 'homeTeam', 'awayTeam', 'venue') */
  key: string;
  /** Display label for the relationship */
  label: string;
  /** Target entity type name (e.g., 'Team', 'Venue') */
  target: string;
  /** Relationship cardinality (defaults to 'one') */
  cardinality?: 'one' | 'many';
  /** Whether this relation is required */
  required?: boolean;
};

/**
 * API client interface for dependency injection
 */
export interface ApiClient {
  apiRequest: <T>(
    endpoint: string,
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: any,
    customHeaders?: Record<string, string>,
    options?: ApiRequestOptions
  ) => Promise<T>;
}

/**
 * Adapter factory interface for entity data operations
 * Uses dependency injection pattern to receive API client
 */
export type EntityAdapterFactory = (apiClient: ApiClient) => EntityAdapter;

/**
 * Adapter interface for entity data operations
 */
export type EntityAdapter = {
  /** Fetch entity by ID */
  findById: (id: string) => Promise<any>;
  /** Search entities by query string. An empty string returns smart default results. */
  search: (q: string, limit?: number) => Promise<SearchResult[]>;
  /** Create a new entity */
  create?: (data: any) => Promise<any>;
  /** Update an existing entity */
  update?: (id: string, data: any) => Promise<any>;
  /** Delete an entity */
  delete?: (id: string) => Promise<void>;
};

/**
 * Complete entity definition including attributes, relations, and UI configuration
 */
export type EntityDefinition = {
  /** Entity type identifier (e.g., 'Player', 'Team', 'Match', 'Venue') */
  type: string;
  /** Human-readable display name */
  displayName: string;
  /** Plural form of the display name */
  displayNamePlural?: string;
  /** Array of entity attributes */
  attributes: AttributeDef[];
  /** Optional relationships to other entities */
  relations?: RelationDef[];
  /** Data adapter factory for CRUD operations (receives API client via DI) */
  adapterFactory: EntityAdapterFactory;
  /** Function to generate display label from entity data */
  labelGenerator?: (entity: any) => string;
  /** UI component configuration */
  ui: {
    /** Component name for entity selection (e.g., 'EntitySelect/Player') */
    entitySelectComponent: string;
    /** Optional component for attribute selection */
    attributeSelectComponent?: string;
    /** Icon ReactNode for UI display */
    icon?: React.ReactNode;
    /** Color theme for UI */
    color?: string;
  };
  /** Metadata for the entity */
  meta?: {
    /** Description of the entity */
    description?: string;
    /** Tags for categorization */
    tags?: string[];
    /** Version of the entity definition */
    version?: string;
  };
};

/**
 * Registry for managing entity type definitions
 * Provides registration, lookup, and validation for data entities
 */
export class EntityRegistry {
  /** Internal storage for entity definitions */
  private definitions = new Map<string, EntityDefinition>();

  /**
   * Register a new entity definition
   * @param def - Entity definition to register
   * @throws Error if entity type is already registered
   */
  public register(def: EntityDefinition): void {
    if (this.definitions.has(def.type)) {
      throw new Error(`Entity type '${def.type}' is already registered`);
    }
    this.definitions.set(def.type, def);
  }

  /**
   * Get entity definition by type
   * @param type - Entity type identifier
   * @returns Entity definition or undefined if not found
   */
  public get(type: string): EntityDefinition | undefined {
    return this.definitions.get(type);
  }

  /**
   * Check if entity type is registered
   * @param type - Entity type identifier
   * @returns True if entity type is registered
   */
  public has(type: string): boolean {
    return this.definitions.has(type);
  }

  /**
   * Get all registered entity definitions
   * @returns Array of all entity definitions
   */
  public getAll(): EntityDefinition[] {
    return Array.from(this.definitions.values());
  }

  /**
   * Get all registered entity types
   * @returns Array of registered entity type identifiers
   */
  public getTypes(): string[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Find entities by attribute type
   * @param mediaType - Media type to search for
   * @returns Array of entities that have attributes of the specified type
   */
  public findByAttributeType(mediaType: MediaType): EntityDefinition[] {
    return this.getAll().filter(entity =>
      entity.attributes.some(attr => attr.mediaType === mediaType)
    );
  }

  /**
   * Find entities by relation target
   * @param targetType - Target entity type
   * @returns Array of entities that have relations to the specified type
   */
  public findByRelationTarget(targetType: string): EntityDefinition[] {
    return this.getAll().filter(entity =>
      entity.relations?.some(rel => rel.target === targetType)
    );
  }

  /**
   * Unregister an entity type
   * @param type - Entity type identifier
   * @returns True if entity was unregistered, false if not found
   */
  public unregister(type: string): boolean {
    return this.definitions.delete(type);
  }

  /**
   * Clear all registered entities
   */
  public clear(): void {
    this.definitions.clear();
  }

  /**
   * Get count of registered entities
   */
  public size(): number {
    return this.definitions.size;
  }

  /**
   * Create an adapter instance for a specific entity type with injected API client
   * @param type - Entity type identifier
   * @param apiClient - API client instance to inject
   * @returns EntityAdapter instance or undefined if entity type not found
   */
  public createAdapter(type: string, apiClient: ApiClient): EntityAdapter | undefined {
    const definition = this.get(type);
    if (!definition) {
      return undefined;
    }
    return definition.adapterFactory(apiClient);
  }

  /**
   * Create adapter instances for all registered entities with injected API client
   * @param apiClient - API client instance to inject
   * @returns Map of entity type to adapter instance
   */
  public createAllAdapters(apiClient: ApiClient): Map<string, EntityAdapter> {
    const adapters = new Map<string, EntityAdapter>();
    this.definitions.forEach((definition, type) => {
      adapters.set(type, definition.adapterFactory(apiClient));
    });
    return adapters;
  }

  /**
   * Generate display label for an entity using its registered labelGenerator
   * @param entityType - Entity type identifier
   * @param entity - Entity data object
   * @returns Generated label string or fallback label
   */
  public generateLabel(entityType: string, entity: any): string {
    const definition = this.get(entityType);
    if (!definition) {
      return `Unknown ${entityType}`;
    }

    // Use custom labelGenerator if available
    if (definition.labelGenerator) {
      try {
        return definition.labelGenerator(entity);
      } catch (error) {
        console.warn(`Error generating label for ${entityType}:`, error);
      }
    }

    // Fallback to generic label generation
    return this.generateFallbackLabel(entity);
  }

  /**
   * Generic fallback label generation for entities without custom labelGenerator
   * @param entity - Entity data object
   * @returns Generated fallback label
   */
  private generateFallbackLabel(entity: any): string {
    if (!entity) return 'Unbekannte Entität';

    // Try common label fields in order of preference
    const labelFields = ['name', 'title', 'displayName', 'label'];
    for (const field of labelFields) {
      if (entity[field] && typeof entity[field] === 'string') {
        return entity[field];
      }
    }

    // Try name combinations
    if (entity.firstName && entity.lastName) {
      return `${entity.firstName} ${entity.lastName}`;
    }

    // Use ID as last resort
    if (entity.id) {
      return `ID: ${entity.id}`;
    }

    return 'Unbekannte Entität';
  }

  /**
   * Get icon for an entity type with optional className styling
   * @param entityType - Entity type identifier
   * @param className - Optional CSS classes for styling (e.g., "w-4 h-4")
   * @returns React icon component with applied className
   */
  public getEntityIcon(entityType: string, className?: string): React.ReactNode {
    const definition = this.get(entityType);
    
    // If entity has a custom icon, clone it with className
    if (definition?.ui?.icon) {
      return React.cloneElement(definition.ui.icon as React.ReactElement, {
        className: className || "w-4 h-4"
      });
    }
    
    // Default fallback icon
    return React.createElement(OctagonAlert, { className: className || "w-4 h-4" });
  }

  /**
   * Get display name for an entity type
   * @param entityType - Entity type identifier
   * @returns Display name or "Undefined" if entity type not found
   */
  public getEntityDisplayName(entityType: string): string {
    const definition = this.get(entityType);
    
    if (definition?.displayName) {
      return definition.displayName;
    }
    
    return "Undefined";
  }
}
